import { useCallback, useEffect, useRef, useState } from "react";

export interface ElementSize {
  width: number;
  height: number;
}

export interface UseElementSizeResult<T extends HTMLElement = HTMLElement> {
  ref: (node: T | null) => void;
  width: number;
  height: number;
}

/**
 * useElementSize
 *
 * A performant hook that tracks the dimensions of a DOM element using ResizeObserver.
 * Returns a callback ref to attach to the element and its current width/height.
 *
 * Performance optimizations:
 * - Uses ResizeObserver for efficient resize detection
 * - Debounces updates using requestAnimationFrame
 * - Cleans up observer on unmount
 *
 * @example
 * ```tsx
 * const { ref, width, height } = useElementSize();
 *
 * return (
 *   <div ref={ref}>
 *     Width: {width}px, Height: {height}px
 *   </div>
 * );
 * ```
 */
export function useElementSize<T extends HTMLElement = HTMLElement>(): UseElementSizeResult<T> {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);
  const rafIdRef = useRef<number | undefined>(undefined);

  const ref = useCallback((node: T | null) => {
    // Cleanup previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Cancel any pending animation frame
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = undefined;
    }

    if (!node) {
      setSize({ width: 0, height: 0 });
      return;
    }

    // Set initial size
    const initialRect = node.getBoundingClientRect();
    setSize({ width: initialRect.width, height: initialRect.height });

    // Create ResizeObserver
    observerRef.current = new ResizeObserver((entries) => {
      // Cancel any pending animation frame
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      // Use requestAnimationFrame to debounce rapid resize events
      rafIdRef.current = requestAnimationFrame((_time) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setSize((prev) => {
            // Only update if dimensions actually changed to prevent unnecessary re-renders
            if (prev.width !== width || prev.height !== height) {
              return { width, height };
            }
            return prev;
          });
        }
      });
    });

    observerRef.current.observe(node);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return { ref, width: size.width, height: size.height };
}
